import { useNavigate } from "react-router-dom"
import styled from "styled-components"
import { MdLogout, MdDarkMode, MdLightMode } from "react-icons/md"
import { useAuthProvider } from "../../Providers/AuthProvider"
import { MobergButton, MobergButtonShape } from "../../Components/MobergButton/MobergButton"
import { MobergTheme } from "../../Components/MobergThemes/MobergColors"
import { MobergFontFamily } from "../../Components/MobergFont/MobergFont"
import { useMobergTheme } from "../../Providers/MobergThemeProvider"
import { useEnvironmentVariablesProvider } from "../../Providers/EnvironmentVariablesProvider"
import { FRONTEND_LINKS } from "../../Constants/FrontendLinks"
import { SOFTWARE_VERSION } from "../../Version"
import logoIcon from "../../assets/images/icon.png"
import darkLogoIcon from "../../assets/images/dark-icon.png"

export default function InternalPageHeader() {
    const authProvider = useAuthProvider()
    const navigate = useNavigate()
    const { colorScheme, setColorScheme } = useMobergTheme()
    const { environmentVariables } = useEnvironmentVariablesProvider()
    const isDark = colorScheme === "dark"

    function handleLogout() {
        authProvider.logout()
        navigate(FRONTEND_LINKS.MAIN)
    }

    function handleLogoClick() {
        navigate(FRONTEND_LINKS.SUBPAGES.DATA.SUBPAGES.VISUALIZE.MAIN)
    }

    return (
        <HeaderContainer>
            <LeftSection>
                <LogoButton onClick={handleLogoClick}>
                    <img
                        src={isDark ? darkLogoIcon : logoIcon}
                        alt="Logo"
                        style={isDark ? { height: 32, width: "auto", marginRight: 12 } : { width: 42, height: 42, marginRight: 12 }}
                    />
                </LogoButton>
                <BrandTitle>MCP Backstage</BrandTitle>
                <InlineVersion>v{SOFTWARE_VERSION}</InlineVersion>
            </LeftSection>

            <RightSection>
                <WorkspaceChip>{window.env.REACT_APP_WORKSPACE_NAME}</WorkspaceChip>
                <Divider />
                {environmentVariables?.FEATURE_FLAG_DARK_MODE && (
                    <ThemeToggleButton
                        onClick={() => setColorScheme(isDark ? "light" : "dark")}
                        shape={MobergButtonShape.SQUARE}
                        aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
                    >
                        {isDark ? <MdLightMode size={22} /> : <MdDarkMode size={22} />}
                    </ThemeToggleButton>
                )}
                <MobergButton onClick={handleLogout} shape={MobergButtonShape.SQUARE} theme={MobergTheme.RED}>
                    <MdLogout size={22} />
                </MobergButton>
            </RightSection>
        </HeaderContainer>
    )
}

const HeaderContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 16px;
    background: var(--color-surface-panel);
    border-bottom: 1px solid var(--color-border-light);
    height: 60px;
    position: relative;
    z-index: 100;
`

const LeftSection = styled.div`
    display: flex;
    align-items: center;
    margin-left: 16px;
    gap: 16px;
`

const LogoButton = styled.button`
    background: transparent;
    border: none;
    padding: 0;
    cursor: pointer;
    display: flex;
    align-items: center;
    transition: opacity 0.2s ease;

    &:hover {
        opacity: 0.8;
    }
`

const BrandTitle = styled.h1`
    font-family: ${MobergFontFamily.HEADER};
    font-weight: 700;
    font-size: 22px;
    color: var(--text-color);
    letter-spacing: 0.04em;
    margin: 0;
    line-height: 1;
`

const RightSection = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
`

const WorkspaceChip = styled.span`
    font-family: ${MobergFontFamily.REGULAR};
    font-size: 12px;
    font-weight: 600;
    color: var(--color-accent-interactive);
    background: var(--color-hover-neutral);
    padding: 4px 12px;
    border-radius: 12px;
    letter-spacing: 0.04em;
    border: 1px solid var(--color-border-light);
`

const InlineVersion = styled.span`
    font-family: ${MobergFontFamily.REGULAR};
    font-size: 11px;
    font-weight: 500;
    color: var(--color-text-version);
    letter-spacing: 0.04em;
    align-self: flex-end;
    margin-bottom: 2px;
`

const Divider = styled.div`
    height: 32px;
    width: 1.5px;
    background: var(--color-border-default);
    border-radius: 1px;
`

const ThemeToggleButton = styled(MobergButton)`
    color: var(--text-secondary-color);

    &:hover {
        color: var(--color-accent-interactive);
    }
`
